import { Box } from '@mui/material';
import { useStyles } from './TreatmentPlan.styles';
import { Icons } from '../../../../../icons';
import { btnSize, btnType, Button } from '../../../../atoms/button';
import { iconBtnPosition, iconBtnType, IconButton } from '../../../../molecules/icon-button';
import visitFreq from '../../../../../../assets/images/visit-frequency.png';
import clsx from 'clsx';
import { colors } from '../../../../../colors';
import dayjs from '../../../../../../utils/dayjs';
import { TreatmentPlanStatusBagde, TreatmentPlanStatusText } from '../../../../../constants/CommonConstants';
import { Badge, badgeStyle, badgeType } from '../../../../atoms/badge';
import { Menu } from '../../../../molecules/menu';
import { Tooltip } from '../../../../atoms/tooltip';
import React, { useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { ProfileInfo } from '../../../profile-info';
import Typography from '../../../../../../components/v2/Typography';
import { typography } from '../../../../../../components/v2/Typography/index.constant';
import { useNavigate } from 'react-router-dom';
import { Modalities } from './Modalities';

const TreatmentPlan = ({
  onAddEditModalities,
  onEditProblem,
  onEditGoal,
  onAddGoal,
  onDeleteGoal,
  onDeleteProblem,
  onEditTreatmentPlan,
  onAddProblem,
  onToggleObjectiveCompletion,
  modalities,
  treatmentPlan,
  // waitingForAI,
  patient,
  hideModalities,
  editRestricted,
  recommendations,
}) => {
  const classes = useStyles();
  const componentRef = useRef();
  const navigate = useNavigate();

  // Check if max problems, goals, objectives are reached
  const hasMaxProblems = treatmentPlan?.problems?.length >= 2;

  const handlePrint = useReactToPrint({
    content: () => {
      const currentComponent = componentRef.current;
      if (currentComponent) {
        currentComponent.style.overflow = 'visible';
      }
      return currentComponent;
    },
  });


  const renderPlan = () => {
    const startDate = dayjs(treatmentPlan.startDate);
    const expiryDate = dayjs(treatmentPlan.expiryDate);
    const now = dayjs();
    const expiresInDays = Math.abs(expiryDate.diff(now, 'day'));
    const isExpired = expiryDate.isBefore(now);

    const createdToday = dayjs(treatmentPlan.createdAt).isToday();
    const updatedToday = dayjs(treatmentPlan.lastModified).isToday();
    const created = createdToday
      ? `Today, ${dayjs(treatmentPlan.createdAt).format('h:mm a')}`
      : dayjs(treatmentPlan.createdAt).format('M/D/YY, h:mm a');
    const updated = updatedToday
      ? `Today, ${dayjs(treatmentPlan.lastModified).format('h:mm a')}`
      : dayjs(treatmentPlan.lastModified).format('M/D/YY, h:mm a');
    const validUntil = dayjs(treatmentPlan.expiryDate).format('M/D/YY, h:mm a');
    return (
      <Box className={classes.container} ref={componentRef}>
        <Box className={classes.produreContainer}>
          {!!patient ? (
            <Box className={classes.header}>
              <Box display="flex" flexDirection="column" gap="4px" flex={1}>
                <Box display="flex" gap="8px" alignItems="center">
                  <span>{treatmentPlan.name}</span>
                  <Badge
                    style={isExpired ? badgeStyle.CRITICAL : badgeStyle.PROVIDER}
                    variant={badgeType.OUTLINED}
                    size={28}
                    className={classes.badge}
                  >
                    Active
                  </Badge>
                </Box>
                <span className={classes.subtitleWrapper}>
                  {isExpired ? `Expired ${expiresInDays} days ago` : `Expires in ${expiresInDays} days`}
                </span>
              </Box>
              <Box display="flex" flexDirection="row" gap="16px" alignItems="center" displayPrint="none">
                <Box display="flex" flexDirection="row" gap="24px" alignItems="center">
                  <Box className={classes.dateWrapper}>
                    <span>Created </span>
                    <span className={classes.subtitleWrapper}>{startDate.format('M/D/YY')}</span>
                  </Box>
                  <Box className={classes.dateWrapper}>
                    <span>Valid until </span>
                    <span className={classes.subtitleWrapper}>{expiryDate.format('M/D/YY')}</span>
                  </Box>
                </Box>
                {!editRestricted && (
                  <Menu
                    icon="more"
                    className={classes.menuButton}
                    itemsWrapperClassName={classes.menuItemsWrapper}
                    items={[
                      {
                        label: 'Edit Plan',
                        onClick: onEditTreatmentPlan,
                      },
                      ...(hasMaxProblems
                        ? []
                        : [
                          {
                            label: 'Add a problem',
                            onClick: onAddProblem,
                          },
                        ]),
                    ]}
                  />
                )}
                <IconButton
                  size={btnSize.SMALL}
                  variant={btnType.OUTLINE}
                  icon="print"
                  className={classes.printBtn}
                  onClick={() => {
                    handlePrint();
                  }}
                >
                  Print
                </IconButton>
              </Box>
            </Box>
          ) : (
            <Box className={classes.header}>
              <Box display="flex" flexDirection="column" gap="4px" flex={1}>
                <Box display="flex" gap="8px" alignItems="center">
                  <span>{treatmentPlan.name}</span>
                  <Badge
                    style={TreatmentPlanStatusBagde[treatmentPlan.status]}
                    variant={badgeType.OUTLINED}
                    size={28}
                    className={classes.badge}
                  >
                    {TreatmentPlanStatusText[treatmentPlan.status]}
                  </Badge>
                </Box>
                <span className={classes.subtitleWrapper}>
                  {isExpired ? `Expired ${expiresInDays} days ago` : `Expires in ${expiresInDays} days`}
                </span>
              </Box>
              <Box display="flex" flexDirection="row" gap="16px" alignItems="center">
                <Box display="flex" flexDirection="row" gap="24px" alignItems="center">
                  <Box className={classes.dateWrapper}>
                    <span>Created </span>
                    <span className={classes.subtitleWrapper}>{startDate.format('M/D/YY')}</span>
                  </Box>
                  <Box className={classes.dateWrapper}>
                    <span>Valid until </span>
                    <span className={classes.subtitleWrapper}>{expiryDate.format('M/D/YY')}</span>
                  </Box>
                </Box>
                <Menu
                  icon="more"
                  className={classes.menuButton}
                  itemsWrapperClassName={classes.menuItemsWrapper}
                  items={[
                    {
                      label: 'Edit Plan',
                      onClick: onEditTreatmentPlan,
                    },
                    ...(hasMaxProblems
                      ? []
                      : [
                        {
                          label: 'Add a problem',
                          onClick: onAddProblem,
                        },
                      ]),
                  ]}
                />
              </Box>
            </Box>
          )}
          {!!patient && (
            <Box className={classes.subHeader}>
              <ProfileInfo
                type="provider"
                photo={treatmentPlan?.providerDisplayPicture}
                role={treatmentPlan?.providerRole}
                fullName={treatmentPlan?.providerName}
              />
              <ProfileInfo
                type="member"
                photo={patient.member.photo}
                nickName={patient.member.firstName + ' ' + patient.member.lastName}
                fullName={`${patient.member.uuid} • ${dayjs(patient.dob, 'YYYY-MM-DD').format('MM/DD/YY')}`}
              />
              <Box>
                <Typography {...typography.body.s.semibold}>Created</Typography>
                <Typography {...typography.body.s.medium}>{created}</Typography>
              </Box>
              <Box>
                <Typography {...typography.body.s.semibold}>Updated</Typography>
                <Typography {...typography.body.s.medium}>{updated}</Typography>
              </Box>
              <Box>
                <Typography {...typography.body.s.semibold}>Valid Until</Typography>
                <Typography {...typography.body.s.medium}>{validUntil}</Typography>
              </Box>
            </Box>
          )}
          {treatmentPlan.problems?.map((problem, index) => {
            // Check if max goals are reached for this problem
            const hasMaxGoals = problem.goals?.length >= 3;

            return (
              <Box key={problem.id}>
                <Box className={classes.plancardContainer}>
                  <Box display="flex" alignItems="center" gap="12px" justifyContent="space-between">
                    <Box display="flex" gap="8px">
                      {recommendations?.some(
                        rec => rec.entityType === 'PROBLEM' && rec.entityReference === problem.id,
                      ) && (
                          <Box width={22} maxWidth={22} maxHeight={22}>
                            <img src={visitFreq} alt="Consultation" width="100%" height="100%" />
                          </Box>
                        )}
                      <Box className={classes.planCardSubtitle}>Problem {index + 1}</Box>
                    </Box>
                    <Box maxWidth={24} maxHeight={24}>
                      {!editRestricted && (
                        <Menu
                          icon="more"
                          className={classes.menu}
                          itemsWrapperClassName={classes.menuItemsWrapper}
                          items={[
                            {
                              label: 'Edit problem',
                              onClick: () => {
                                onEditProblem(problem);
                              },
                            },
                            ...(hasMaxGoals
                              ? []
                              : [
                                {
                                  label: 'Add goal',
                                  onClick: () => {
                                    onAddGoal(problem);
                                  },
                                },
                              ]),
                            {
                              label: 'Delete Problem',
                              onClick: () => {
                                onDeleteProblem(problem.id);
                              },
                            },
                          ]}
                        />
                      )}
                    </Box>
                  </Box>
                  <Box className={classes.planCardText}>{problem.detail}</Box>
                </Box>
                {problem.goals?.map((goal, goalIndex) => {
                  // Check if max objectives are reached for this goal

                  return (
                    <Box key={goal.id} className={classes.plancardContainer}>
                      <Box display="flex" alignItems="center" gap="12px" justifyContent="space-between">
                        <Box className={classes.planCardSubtitle}>Goal {goalIndex + 1}</Box>
                        <Box maxWidth={24} maxHeight={24}>
                          {!editRestricted && (
                            <Menu
                              icon="more"
                              className={classes.menu}
                              itemsWrapperClassName={classes.menuItemsWrapper}
                              items={[
                                {
                                  label: 'Edit goal',
                                  onClick: () => {
                                    onEditGoal(goal);
                                  },
                                },
                                {
                                  label: 'Delete goal',
                                  onClick: () => {
                                    onDeleteGoal(goal.id);
                                  },
                                },
                              ]}
                            />
                          )}
                        </Box>
                      </Box>
                      <Box className={classes.planCardText} display="flex" flexDirection="column" gap="16px">
                        <Box width="100%" display="flex" flexDirection="column" gap="12px">
                          <Box>{goal.name}</Box>
                          <Box className={classes.subText}>{goal.details}</Box>
                        </Box>
                        <Box className={classes.objective} width="100%">
                          <Box display="flex" flexDirection="row" gap="8px" alignItems="center">
                            {recommendations?.some(
                              rec =>
                              (rec.entityType === 'OBJECTIVE' &&
                                (goal.objectives?.some(obj => obj.id === rec.entityReference) ||
                                  (rec.recommendationType === 'ADD_OBJECTIVE' && rec.entityReference === goal.id))),
                            ) && (
                                <Box>
                                  <Icons
                                    glyph="star"
                                    color={colors.secondary500}
                                    className={classes.visitFrequency}
                                  />
                                </Box>
                              )}
                            <Box className={classes.objectiveTile}>Objectives</Box>
                          </Box>
                          <Box display="flex" flexDirection="column" gap="8px">
                            {goal.objectives.length > 0 ? (
                              goal.objectives?.map((item, index) => (
                                <Box key={item.id} className={classes.objectivesListItem}>
                                  <Tooltip title={item.isCompleted ? 'Completed' : 'Mark as completed'} placement="left">
                                    <Box
                                      onClick={() => 
                                        onToggleObjectiveCompletion && 
                                        onToggleObjectiveCompletion(problem.id, goal.id, item.id, !item.isCompleted)
                                      }
                                      sx={{ cursor: onToggleObjectiveCompletion ? 'pointer' : 'default', display: 'flex' }}
                                    >
                                      <Icons 
                                        glyph={item.isCompleted ? 'check-circle' : 'checked-circle-outlined'} 
                                        className={classes.objectivesCheckIcon} 
                                      />
                                    </Box>
                                  </Tooltip>
                                  <Box>{item.details}</Box>
                                </Box>
                              ))
                            ) : (
                              <Box className={classes.objectivesListItem}>
                                <Box>No objectives found</Box>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            );
          })}
          {treatmentPlan.frequencyOfWorkOutsideSessions && (
            <Box className={classes.plancardContainer}>
              <Box display="flex" alignItems="center" gap="12px" justifyContent="space-between">
                <Box className={classes.planCardSubtitle}>Frequency of patient work outside of sessions</Box>
              </Box>
              <Box className={classes.planCardText}>{treatmentPlan.frequencyOfWorkOutsideSessions}</Box>
            </Box>
          )}
          {treatmentPlan.patientInvolvementInTreatmentPlan && (
            <Box className={classes.plancardContainer}>
              <Box display="flex" alignItems="center" gap="12px" justifyContent="space-between">
                <Box className={classes.planCardSubtitle}>Patient Involvement in treatment plan</Box>
              </Box>
              <Box
                className={clsx(classes.planCardText, {
                  [classes.textSuccess]: treatmentPlan.patientInvolvementInTreatmentPlan.involved,
                  [classes.textError]: !treatmentPlan.patientInvolvementInTreatmentPlan.involved,
                })}
              >
                {treatmentPlan.patientInvolvementInTreatmentPlan.involved ? 'Yes' : 'No'}
              </Box>
              {!treatmentPlan.patientInvolvementInTreatmentPlan.involved &&
                treatmentPlan.patientInvolvementInTreatmentPlan.details?.length > 0 && (
                  <Box className={classes.subText}>
                    {treatmentPlan.patientInvolvementInTreatmentPlan.details}
                  </Box>
                )}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box display="flex" flexDirection="column" gap="24px" flex={2}>
      {treatmentPlan && renderPlan()}
      {!hideModalities && (
        <Modalities
          modalities={modalities}
          onAddEditModalities={onAddEditModalities}
        />
      )}
    </Box>
  );
};
export { TreatmentPlan };
